import { useEffect, useState } from "react";
import { toast } from "react-toastify";
// prime react
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
//services
import { getAccountToPayFiles } from "../../../services/Financial/AccountToPay/getAccountToPayFiles";
import { getTemporaryUrl } from "../../../services/File/getTemporaryUrl";
// components
import Panel from "../../../components/panel/panel";
import ExportTable from "../../../components/button/ExportButton";
import Modal from "../../../components/modal/Modal";
//context
import { useAppContext } from "../../../context/AppContext";
import { useFinancialContext } from "../../../context/FinancialContext";
// utils
import { formatDate } from "../../../utils/formatDate";
import { formatCurrency } from "../../../utils/formatCurrency";

export const TableAccountToPay = ({
  dataTable,
  totalRecords,
  globalFilterValue,
  onGlobalFilterChange,
  isLoading,
}) => {
  const { nomenclatures } = useAppContext();
  const { permissions } = useAppContext();
  const { selectedCustomers, setSelectedCustomers } = useFinancialContext();
  const nomenclature = nomenclatures ? JSON.parse(nomenclatures) : [];
  const [visible, setVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [tableToExport, setTableToExport] = useState(null);

  const viewDocument = (rowData) => {
    viewFile(rowData.id);
    setVisible(true);
  };
  const viewFile = async (id) => {
    setVisible(true);
    setPreviewDoc([]); // Limpa o preview antes de carregar o novo documento
    setIsLoadingDocument(true); // Inicia o estado de carregamento
    const notification = toast.loading("Por favor aguarde, carregando...");
    try {
      const response = await getAccountToPayFiles(id);
      if (response && response.length > 0) {
        const fileData = response[0];
        const fileId = fileData.id;
        // Chamando a API para obter a URL temporária do arquivo
        const tempUrlResponse = await getTemporaryUrl(fileId);
        if (tempUrlResponse && tempUrlResponse.url) {
          const fileUrl = tempUrlResponse.url; // URL do arquivo
          // Atualizando o estado com o documento carregado
          setPreviewDoc([
            {
              uri: fileUrl, // URL obtida da API
              fileType: response[0].path, // Tipo de arquivo recebido
              fileName: response[0].name, // Nome do arquivo recebido
            },
          ]);
          toast.update(notification, {
            render: "Documento Carregado!",
            autoClose: 1000,
            theme: "colored",
            type: "success",
            isLoading: false,
          });
        } else {
          throw new Error("Não foi possível obter a URL temporária");
        }
      } else {
        throw new Error("Não Há Documentos para este Lançamento");
      }
    } catch (error) {
      console.error("Erro ao carregar documento:", error);
      toast.update(notification, {
        render: "Não Há Documentos para este Lançamento",
        autoClose: 1000,
        theme: "colored",
        type: "warning",
        isLoading: false,
      });
    } finally {
      setIsLoadingDocument(false); // Finaliza o carregamento
    }
  };

  const columns = [
    { field: "id", header: "Nº Lançamento" },
    { field: "sequence_number", header: "Nº Parcela" },
    { field: "supplier.name", header: "Fornecedor" },
    { field: "cost_center.desc", header: "Centro de Custo" },
    { field: "description.name", header: "Descrição" },
    {
      field: "status",
      header: "Situação",
      body: (rowData) => {
        let labelClass = "";
        let labelText = "";
        switch (rowData.status) {
          case 0:
            labelClass = "badge bg-warning";
            labelText = "Em Aberto";
            break;
          case 1:
            labelClass = "badge bg-success";
            labelText = "Pago";
            break;
          case 2:
            labelClass = "badge bg-dark";
            labelText = "Ib Juridico";
            break;
          case 3:
            labelClass = "badge bg-danger";
            labelText = "Em Renegociação";
            break;
          default:
        }

        return (
          <span
            className={labelClass}
            style={{
              fontSize: "10px",
              marginLeft: "15px",
              marginRight: "-40px",
            }}
          >
            {labelText}
          </span>
        );
      },
    },

    {
      field: "due_date",
      header: "Data vencimento",
      body: (rowData) => formatDate(rowData.due_date),
    },
    {
      field: "value",
      header: "Valor parcela",
      body: (rowData) => formatCurrency(rowData.value),
    },
    {
      field: "block",
      header: `${nomenclature[0]?.value || "Quadra"}/${
        nomenclature[1]?.value || "Lote"
      }`,
    },
    { field: "enterprise.title", header: "Empreendimento" },
    {
      field: "payment_date",
      header: "Data recebimento",
      body: (rowData) => formatDate(rowData.payment_date),
    },
    {
      field: "receipt",
      header: "Código de Barras",
    },

    {
      field: "discount",
      header: "Desconto",
      body: (rowData) => formatCurrency(rowData.discount || 0),
    },
    {
      field: "addition",
      header: "Acréscimos",
      body: (rowData) => formatCurrency(rowData.addition || 0),
    },
    {
      field: "payment_value",
      header: "Valor pago",
      body: (rowData) => formatCurrency(rowData.payment_value || 0),
    },
    { field: "payment_method.name", header: "Forma de pagamento" },
    { field: "released_by.name", header: "Lançado por" },
    { field: "completed_by.name", header: "Baixado por" },
    ...(permissions.includes("178")
      ? [
          {
            field: "actions",
            header: "Documentos",
            body: (rowData) => (
              <Button
                icon="pi pi-eye"
                className="p-button-text"
                onClick={() => viewDocument(rowData)}
                tooltip="Visualizar Documento"
              />
            ),
          },
        ]
      : []),
    { field: "completed_code", header: "Número de baixa" },
    { field: "obs", header: "Observação" },
  ];

  const exportColumns = columns.map((col) => ({
    title: col.header,
    dataKey: col.field,
  }));

  const onSelectionChange = (event) => {
    const value = event.value;
    setSelectedCustomers(value);
    setSelectAll(value.length === totalRecords);
  };

  const onSelectAllChange = (event) => {
    setSelectAll(event.checked);

    if (event.checked) {
      setSelectedCustomers(dataTable);
    } else {
      setSelectedCustomers([]);
    }
  };

  useEffect(() => {
    if (dataTable) {
      const dataWithoutIds = dataTable.map((t) => {
        let labelText = "";
        switch (t.status) {
          case 0:
            labelText = "Em Aberto";
            break;
          case 1:
            labelText = "Pago";
            break;
          case 2:
            labelText = "Ib Juridico";
            break;
          case 3:
            labelText = "Em Renegociação";
            break;
          default:
        }

        return {
          numeroLancamento: t.id,
          numeroParcela: t.sequence_number,
          NomeFornecedor: t.supplier?.name,
          CentroCusto: t.cost_center?.desc,
          Descricao: t.description?.name,
          Situacao: labelText,
          DataVencimento: t.due_date,
          ValorParcela: t.value,
          QuadraLote: t.block,
          Empreendimento: t.enterprise?.title,
          DataRecebimento: t.completed_date,
          CodBarras: t.receipt,
          Desconto: t.discunt,
          Acrescimos: t.addition,
          ValorPago: t.payment_value,
          FormaPagamento: t.payment_method?.name,
          LancadoPor: t.released_by?.name,
          BaixadoPor: t.completed_by?.name,
        };
      });
      setTableToExport(dataWithoutIds);
    }
  }, [dataTable]);

  return (
    <>
      <Panel mainCss="mt-4">
        <div className="px-2 pb-3">
          <ExportTable
            tableData={tableToExport}
            exportColumns={exportColumns}
            tabelaKey="Relatório de Contas a Pagar"
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            orientation="landscape"
          />
          <DataTable
            value={dataTable}
            loading={isLoading}
            stripedRows
            showGridlines
            sortMode="multiple"
            selectionMode="multiple"
            selectAll={selectAll}
            selection={selectedCustomers}
            onSelectionChange={onSelectionChange}
            onSelectAllChange={onSelectAllChange}
            globalFilter={globalFilterValue}
            emptyMessage="Nenhuma Informação foi encontrada."
            tableStyle={{ minWidth: "0.8rem", fontSize: "0.7rem" }}
          >
            <Column
              field="id"
              headerStyle={{ width: "3rem" }}
              selectionMode="multiple"
            />
            {columns.map((col, i) => (
              <Column
                sortable
                body={col.body}
                key={i}
                style={{ minWidth: 150 }}
                field={col.field}
                header={col.header}
              />
            ))}
          </DataTable>
        </div>
      </Panel>
      <Modal
        dynamicTitle="Visualização do Documento de Pagamento"
        visible={visible}
        style={{ width: "80vw" }}
        onHide={() => setVisible(false)}
      >
        {isLoadingDocument ? (
          <p style={{ textAlign: "center", fontSize: "1.2rem", color: "#666" }}>
            Carregando documento...
          </p>
        ) : previewDoc.length > 0 ? (
          previewDoc[0].fileType === "pdf" ? (
            <iframe
              src={previewDoc[0].uri}
              style={{ width: "100%", height: "75vh", border: "none" }}
            ></iframe>
          ) : (
            <img
              src={previewDoc[0].uri}
              alt="Documento"
              style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          )
        ) : (
          <p style={{ textAlign: "center", fontSize: "1.2rem", color: "#666" }}>
            Não há documentos para exibir.
          </p>
        )}
      </Modal>
    </>
  );
};
